/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import type { ReactNode } from 'react';

import { createIllustration } from '@jira/illustrations__illustration/src/ui/index.tsx';
import { jsx } from '@atlaskit/css';
import { FormattedMessage } from '@atlassian/jira-intl';

import { JiraUpsellCard } from './JiraUpsellCard.tsx';
import plansLight from './plans-light.svg';
import plansDark from './plans-dark.svg';

const PlansIllustration = createIllustration({ srcLight: plansLight, srcDark: plansDark });

export type PlansUpsellCardProps = {
	/**
	 * The primary CTA button element. Pass a fully-formed button so the caller
	 * controls the button's lifecycle, analytics, and entry-point wiring.
	 * e.g. `<PremiumUpgradeModalEntryPointButton>Try it free</PremiumUpgradeModalEntryPointButton>`
	 */
	primaryButton: ReactNode;
	/**
	 * The secondary button element. Pass a fully-formed button or link so the
	 * caller controls the label, href, analytics, and navigation behaviour.
	 * e.g. `<LinkButton href="..." target="_blank">Learn more</LinkButton>`
	 */
	secondaryButton: ReactNode;
};

/**
 * An upsell card for the Plans feature, shown to free Jira users who discover Plans.
 *
 * Renders the shared {@link JiraUpsellCard} with `appearance="embedded"` because
 * the Plans variant lives inside an ADS `FlyoutMenuItemContent` (the Plans L1
 * flyout in nav4). The embedded appearance removes the card's own chrome
 * (border, shadow, background, fixed width) and tunes spacing/sizing so the
 * card sits flush inside the flyout.
 */
export const PlansUpsellCard = ({
	primaryButton,
	secondaryButton,
}: PlansUpsellCardProps): JSX.Element => (
	<JiraUpsellCard
		appearance="embedded"
		heading={
			<FormattedMessage
				id="growth.feature-gates.upsell-card.plans-heading"
				defaultMessage="See all your timelines in one plan"
				description="Heading for the upsell card shown when free users discover the Plans feature"
			/>
		}
		body={
			<FormattedMessage
				id="growth.feature-gates.upsell-card.plans-body"
				defaultMessage="Align all your timelines and teams in one view. Try free for 30 days with Premium."
				description="Body text for the Plans upsell card explaining the feature and trial offer"
			/>
		}
		illustration={PlansIllustration}
		primaryButton={primaryButton}
		secondaryButton={secondaryButton}
	/>
);
